import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ApolloError } from '@apollo/client';
import { getApplicationError, getValidationErrors } from '@oysterjs/core/graphql/errors';

import { client } from '~/api/client';
import {
  type CaraWorkspaceFieldsFragment,
  UpdateCaraWorkspacesDocument,
  type UpdateCaraWorkspacesMutation
} from '~/api/graphql';

/**
 * Owns the "archive workspace" mutation for the workspace detail page.
 * Mirrors the pattern of `useStopCaraWorkspace`: invokes the mutation against
 * the apollo client directly, then navigates back to the workspace list on
 * success since the current workspace is no longer accessible from this view.
 */
export function useArchiveCaraWorkspace(args: {
  workspace: CaraWorkspaceFieldsFragment | undefined;
}) {
  const { workspace } = args;
  const navigate = useNavigate();
  const [archiveLoading, setArchiveLoading] = React.useState(false);
  const [archiveError, setArchiveError] = React.useState<string>();

  const archiveCaraWorkspace = React.useCallback(async (): Promise<boolean> => {
    if (!workspace || archiveLoading) return false;

    setArchiveLoading(true);
    setArchiveError(undefined);

    try {
      const res = await client.mutate<UpdateCaraWorkspacesMutation>({
        mutation: UpdateCaraWorkspacesDocument,
        variables: { workspaceIds: [workspace.id], input: { archive: true } }
      });

      const applicationError = getApplicationError(new ApolloError({ graphQLErrors: res.errors }));
      const validationErrors = getValidationErrors(new ApolloError({ graphQLErrors: res.errors }));

      if (applicationError) {
        setArchiveError(applicationError.message);
        return false;
      }
      if (validationErrors?.length) {
        setArchiveError(validationErrors[0].message);
        return false;
      }

      navigate('/app/workspaces');
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setArchiveError('Failed to archive workspace');
      return false;
    } finally {
      setArchiveLoading(false);
    }
  }, [workspace, archiveLoading, navigate]);

  return { archiveLoading, archiveError, archiveCaraWorkspace };
}
